import {
  Button,
  CardBody,
  HeaderWithAlert,
  ModalConfirmation,
  PopUpInformative,
} from 'components';
import FormCouponPrice from 'components/organisms/Form-cupon-price/Form-cupon-price';
import { useFormCuponPrice } from 'components/organisms/Form-cupon-price/hooks/Form-cupon-price.hook';
import { IAppReducer } from 'models/reducer.interface';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPropertyUserList } from 'shared/services/user-add-subscription';
import './Add-subscription.scss';
import AvailableAddons from './componentes/AvailableAddons';
import CardPrice from './componentes/CardPrice';
import { CompanySelector } from './componentes/CompanySelector';
import { PaymentMethod, PaymentMethodRef } from './componentes/PaymentMethod';
import { PropertyDetails } from './componentes/PropertyDetails';
import { getYearEligibility, YearRange } from './componentes/PropertyList';
import { PurchaseSummary } from './componentes/PurchaseSummary';
import { SubscriptionPeriodSelector } from './componentes/SubscriptionPeriodSelector';
import {
  getCompanyDisplayName,
  useAddSubscription,
} from './hooks/Add-subscription.hook';
import { ApiProperty, Price } from './types/Add-subscription.types';

/** Slots de tipo vehículo (uno por cada cantidad de cada precio) para mostrar un select por producto */
interface VehicleSlot {
  key: string;
  priceId: number;
  stripePriceId: string;
  aliasName: string;
  count: number;
  /** Nombre del tipo de producto (Vehiculos | Motocicleta) para filtrar por property.type */
  productTypeName: string;
}

function getVehicleSlots(
  products: { prices: Price[] }[],
  counts: Record<number, number>,
  period: string
): VehicleSlot[] {
  const slots: VehicleSlot[] = [];
  products.forEach((product) => {
    product.prices.forEach((price) => {
      if (price.periocity_id.name !== period) return;
      const count = counts[price.id] || 0;
      if (count <= 0) return;
      const type = price.product_type_id.name.toLowerCase();
      if (type !== 'vehiculos' && type !== 'motocicleta') return;
      for (let i = 1; i <= count; i++) {
        slots.push({
          key: `${price.id}-${i}`,
          priceId: price.id,
          stripePriceId: price.stripe_price_id,
          aliasName: price.alias_name,
          count: i,
          productTypeName: price.product_type_id.name,
        });
      }
    });
  });
  return slots;
}

/** Filtra propiedades cuyo type coincide con el tipo del price (vehículo vs motocicleta) */
function filterByPropertyType(
  list: ApiProperty[],
  productTypeName: string
): ApiProperty[] {
  const typeLower = productTypeName.toLowerCase();
  return list.filter((p) => {
    const propType = (p.type || '').toLowerCase();
    if (typeLower === 'motocicleta')
      return propType === 'motocicleta' || propType.includes('moto');
    if (typeLower === 'vehiculos')
      return (
        propType === 'vehiculos' ||
        propType.includes('vehiculo') ||
        propType === 'auto'
      );
    return true;
  });
}

/** Filtra propiedades que ya tienen este producto (stripe_price_id) en linked */
function filterAvailableForProduct(
  list: ApiProperty[],
  stripePriceId: string
): ApiProperty[] {
  return list.filter(
    (p) => !p.linked?.some((l) => l.userProductStripePriceId === stripePriceId)
  );
}

/** Rango de años permitido para un price (igual lógica que PropertyDetails/CardPrice) */
function getYearRangeFromPrice(price: Price): YearRange | null {
  if (typeof price.max_property_age !== 'number') return null;
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - price.max_property_age;
  const minYear =
    price.min_property_age == null || price.min_property_age === 0
      ? currentYear
      : currentYear - price.min_property_age;
  return { minYear, maxYear };
}

/** Excluye de la lista los vehículos ya elegidos en otros slots del mismo price (evita duplicar el mismo auto en Vehículo 1 y Vehículo 2) */
function excludeAlreadySelectedInSamePrice(
  list: ApiProperty[],
  slot: VehicleSlot,
  vehicleSlots: VehicleSlot[],
  selectedPropertyByKey: Record<string, ApiProperty | null>
): ApiProperty[] {
  const selectedUidsInOtherSlots = vehicleSlots
    .filter((s) => s.priceId === slot.priceId && s.key !== slot.key)
    .map((s) => selectedPropertyByKey[s.key]?.uid)
    .filter((uid): uid is string => Boolean(uid));
  if (selectedUidsInOtherSlots.length === 0) return list;
  return list.filter((p) => !selectedUidsInOtherSlots.includes(p.uid));
}

export const AddSubscription = () => {
  const { branchReducer, authReducer: user } = useSelector(
    (state: IAppReducer) => state
  );
  const [userPropertiesList, setUserPropertiesList] = useState<ApiProperty[]>(
    []
  );
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [errorProperties, setErrorProperties] = useState(false);
  const [selectedPropertyByKey, setSelectedPropertyByKey] = useState<
    Record<string, ApiProperty | null>
  >({});

  useEffect(() => {
    if (!user.uid) return;
    let cancelled = false;
    setLoadingProperties(true);
    setErrorProperties(false);
    getPropertyUserList(user.uid)
      .then((data) => {
        if (!cancelled) setUserPropertiesList(data);
      })
      .catch(() => {
        if (!cancelled) setErrorProperties(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingProperties(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user.uid]);

  /* coupon */
  const {
    couponError,
    appliedCoupon,
    couponCode,
    loadingCoupon,
    resetCoupon,
    handleCouponCode,
    handleApplyCoupon,
  } = useFormCuponPrice();

  /* credit card */
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string | null>(null);
  // CB-18: true cuando el selector de método de pago reporta 2+ tarjetas Stripe sin ninguna
  // marcada como predeterminada — bloquea el CTA de "Comprar" hasta que se corrija.
  const [hasNoDefaultCardIssue, setHasNoDefaultCardIssue] = useState(false);
  // CB-18: true mientras se confirma un cambio de tarjeta predeterminada dentro del selector —
  // bloquea "Comprar" para no disparar la compra con una selección a medio confirmar.
  const [isCardActionPending, setIsCardActionPending] = useState(false);
  const paymentMethodRef = useRef<PaymentMethodRef>(null);

  const {
    steps,
    navigate,
    products,
    vehicleData,
    vehicleDataError,
    retryLoadVehicleData,
    loading,
    error,
    period,
    counts,
    selectedAddons,
    isFormValid,
    formData,
    handleCount,
    handleAddonToggle,
    calculatePrice,
    formatCurrency,
    getAdvantages,
    handleFormValidChange,
    handlePurchase,
    isAnyProductSelected,
    setPeriod,
    setFormData,
    subtotal,
    isPurchasing,
    showAlertError,
    setShowAlertError,
    companySelectorStatus,
    companies,
    selectedCompanyId,
    handleCompanyChange,
    retryLoadProducts,
    successCompanyName,
    finishPurchase,
  } = useAddSubscription({
    appliedCoupon,
    selectedPaymentMethod,
    selectedPropertyByKey,
  });

  const vehicleSlots = useMemo(
    () =>
      getVehicleSlots(
        products.filter((p) => !p.is_addon),
        counts,
        period
      ),
    [products, counts, period]
  );

  const hasNonVehicleEntries = useMemo(() => {
    return products.some((p) =>
      p.prices.some(
        (price) =>
          price.periocity_id.name === period &&
          (counts[price.id] || 0) > 0 &&
          price.product_type_id.name.toLowerCase() !== 'vehiculos' &&
          price.product_type_id.name.toLowerCase() !== 'motocicleta'
      )
    );
  }, [products, period, counts]);

  const setPropertyForSlot = (
    key: string,
    property: ApiProperty | null | undefined
  ) => {
    setSelectedPropertyByKey((prev) => {
      const next = { ...prev };
      if (property === undefined) {
        delete next[key];
        return next;
      }
      next[key] = property;
      return next;
    });
  };

  const allPrices = useMemo(
    () => products.flatMap((p) => p.prices),
    [products]
  );

  const propertyListPropsByKey = useMemo(() => {
    const map: Record<
      string,
      {
        list: ApiProperty[];
        yearRange: ReturnType<typeof getYearRangeFromPrice>;
        stripePriceId: string;
      }
    > = {};
    vehicleSlots.forEach((slot) => {
      const listByType = filterByPropertyType(
        userPropertiesList,
        slot.productTypeName
      );
      const list = excludeAlreadySelectedInSamePrice(
        listByType,
        slot,
        vehicleSlots,
        selectedPropertyByKey
      );
      const price = allPrices.find((p) => p.id === slot.priceId);
      const yearRange = price ? getYearRangeFromPrice(price) : null;
      map[slot.key] = {
        list,
        yearRange,
        stripePriceId: slot.stripePriceId,
      };
    });
    return map;
  }, [vehicleSlots, userPropertiesList, selectedPropertyByKey, allPrices]);

  // Limpiar selecciones que ya no son válidas (tipo, mismo price, año no elegible)
  useEffect(() => {
    let changed = false;
    const next: Record<string, ApiProperty | null> = {
      ...selectedPropertyByKey,
    };
    vehicleSlots.forEach((slot) => {
      const selected = selectedPropertyByKey[slot.key];
      if (!selected) return;
      const listByType = filterByPropertyType(
        userPropertiesList,
        slot.productTypeName
      );
      const availableForProduct = filterAvailableForProduct(
        listByType,
        slot.stripePriceId
      );
      const availableList = excludeAlreadySelectedInSamePrice(
        availableForProduct,
        slot,
        vehicleSlots,
        selectedPropertyByKey
      );
      if (!availableList.some((p) => p.uid === selected.uid)) {
        next[slot.key] = null;
        changed = true;
        return;
      }
      if (
        selected.linked?.some(
          (l) => l.userProductStripePriceId === slot.stripePriceId
        )
      ) {
        next[slot.key] = null;
        changed = true;
        return;
      }
      const price = allPrices.find((p) => p.id === slot.priceId);
      const yearRange = price ? getYearRangeFromPrice(price) : null;
      if (!getYearEligibility(selected, yearRange).eligible) {
        next[slot.key] = null;
        changed = true;
      }
    });
    if (changed) setSelectedPropertyByKey(next);
  }, [vehicleSlots, userPropertiesList, selectedPropertyByKey, allPrices]);

  const allVehicleSlotsHaveExistingProperty =
    vehicleSlots.length > 0 &&
    vehicleSlots.every((s) => Boolean(selectedPropertyByKey[s.key]));
  const noFormNeeded =
    allVehicleSlotsHaveExistingProperty && !hasNonVehicleEntries;
  const isPropertyStepValid = noFormNeeded || isFormValid;

  // TEMPORAL — eliminar con CB-186
  // Al cambiar de compañía los prices cambian: se limpian cupón y vehículos elegidos (el hook limpia
  // cantidades, add-ons y formularios). Con productos elegidos se pide confirmación antes.
  const [pendingCompanyId, setPendingCompanyId] = useState<number | null>(
    null
  );
  /** Aplica el cambio de company: limpia cupón y vehículos elegidos y recarga el catálogo. */
  const applyCompanyChange = (companyId: number) => {
    resetCoupon();
    setSelectedPropertyByKey({});
    handleCompanyChange(companyId);
  };
  /** Con productos elegidos pide confirmación antes de cambiar de company. */
  const onChangeCompany = (companyId: number) => {
    if (isAnyProductSelected) {
      setPendingCompanyId(companyId);
      return;
    }
    applyCompanyChange(companyId);
  };

  const mainProducts = products.filter((product) => !product.is_addon);
  const hasPrices = mainProducts.some((product) => product.prices.length > 0);
  const hasPricesForPeriod = mainProducts.some((product) =>
    product.prices.some((price) => price.periocity_id.name === period)
  );
  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);

  // TEMPORAL — eliminar con CB-186
  // Sin selector la vista se comporta como antes: carga y error a pantalla completa y sin los
  // mensajes nuevos.
  const showCompanySelector = companySelectorStatus === 'visible';
  if (companySelectorStatus === 'checking')
    return <div>Cargando productos...</div>;
  if (!showCompanySelector && loading) return <div>Cargando productos...</div>;
  if (!showCompanySelector && (error || vehicleDataError))
    return <div>Hubo un error al traer productos con sus prices</div>;

  return (
    <>
      {showAlertError && (
        <PopUpInformative
          title={'Error'}
          text={showAlertError}
          type="error"
          closePopUp={() => {
            setShowAlertError(null);
            // CB-18: si la compra falló por un problema de tarjeta (ej. la default cambió
            // entremedias, HTTP 400 SELECTED_CARD_NOT_CURRENT_DEFAULT), la lista se queda
            // desactualizada — se refresca sola al cerrar, igual que ya hace edit-subscription.
            paymentMethodRef.current?.refreshCards();
          }}
        ></PopUpInformative>
      )}
      {/* TEMPORAL — eliminar con CB-186 */}
      {successCompanyName !== null && (
        <PopUpInformative
          title="¡Suscripción creada!"
          text={`Suscripción creada en ${successCompanyName}`}
          type="successful"
          labelButton="Continuar"
          onClickButton={finishPurchase}
          closePopUp={finishPurchase}
        ></PopUpInformative>
      )}
      {/* TEMPORAL — eliminar con CB-186 */}
      <ModalConfirmation
        isOpen={pendingCompanyId !== null}
        title="Cambiar la compañía vacía tu selección"
        message="Se quitarán los productos, add-ons, vehículos y el cupón que elegiste, y se cargará el catálogo de la nueva compañía."
        confirmText="Cambiar compañía"
        cancelText="Cancelar"
        onClose={() => setPendingCompanyId(null)}
        onConfirm={() => {
          if (pendingCompanyId !== null) applyCompanyChange(pendingCompanyId);
          setPendingCompanyId(null);
        }}
      />
      <HeaderWithAlert
        steps={steps}
        onClick={() => navigate('/settings')}
      ></HeaderWithAlert>
      <div className="add-subscription__header">
        <h6>Agregar suscripción</h6>
      </div>
      <main
        className="add-subscription__container container__generic"
        role="main"
        aria-label="Agregar suscripción"
      >
        {/* TEMPORAL — eliminar con CB-186 */}
        {showCompanySelector && (
          <CompanySelector
            companies={companies}
            selectedCompanyId={selectedCompanyId}
            userCompanyId={Number(user.companyId)}
            disabled={loading || isPurchasing}
            onChangeCompany={onChangeCompany}
          />
        )}
        {showCompanySelector && loading && <div>Cargando productos...</div>}
        {showCompanySelector && !loading && error && (
          <div className="add-subscription__empty">
            <p>
              No se pudo cargar el catálogo de{' '}
              {getCompanyDisplayName(selectedCompany)}.
            </p>
            <Button
              color="primary"
              size="small"
              template="secondary"
              typeStyle="rounded"
              type="button"
              onClick={retryLoadProducts}
            >
              Reintentar
            </Button>
          </div>
        )}
        {showCompanySelector &&
          !loading &&
          !error &&
          !hasPrices && (
            <p className="add-subscription__empty">
              {`La compañía ${getCompanyDisplayName(
                selectedCompany
              )} no tiene precios disponibles para asociar.`}
            </p>
          )}
        {showCompanySelector && vehicleDataError && (
          <div className="add-subscription__empty">
            <p>No se pudo cargar la lista de marcas y modelos.</p>
            <Button
              color="primary"
              size="small"
              template="secondary"
              typeStyle="rounded"
              type="button"
              onClick={retryLoadVehicleData}
            >
              Reintentar
            </Button>
          </div>
        )}
        {!loading &&
          !error &&
          !vehicleDataError &&
          (hasPrices || !showCompanySelector) && (
          <>
            <SubscriptionPeriodSelector
              period={period}
              onChangePeriod={(value) =>
                setPeriod(value as 'Mensual' | 'Anual')
              }
            />

            <CardBody>
              {showCompanySelector && !hasPricesForPeriod && (
                <p className="add-subscription__empty">
                  No hay precios con periodicidad {period.toLowerCase()} para
                  esta compañía.
                </p>
              )}
              {mainProducts.map((product) => (
                <div key={product.id}>
                  {product.prices
                    .filter((price) => price.periocity_id.name === period)
                    .map((price) => (
                      <CardPrice
                        key={price.id}
                        price={price}
                        product={product}
                        counts={counts}
                        handleCount={handleCount}
                        getAdvantages={getAdvantages}
                        calculatePrice={calculatePrice}
                        formatCurrency={formatCurrency}
                      />
                    ))}
                </div>
              ))}
            </CardBody>

            <AvailableAddons
              getAdvantages={getAdvantages}
              products={products}
              period={period}
              counts={counts}
              selectedAddons={selectedAddons}
              handleAddonToggle={handleAddonToggle}
            />

            {isAnyProductSelected && (
              <PropertyDetails
                counts={counts}
                prices={allPrices}
                period={period}
                onValidChange={handleFormValidChange}
                formData={formData}
                setFormData={setFormData}
                vehicleData={vehicleData}
                branch={branchReducer.branch}
                selectedPropertyByKey={selectedPropertyByKey}
                onSelectPropertyForSlot={setPropertyForSlot}
                propertyListPropsByKey={propertyListPropsByKey}
                loadingProperties={loadingProperties}
                errorProperties={errorProperties}
              />
            )}

            <PaymentMethod
              ref={paymentMethodRef}
              onSelectPaymentMethod={setSelectedPaymentMethod}
              onNoDefaultCardChange={setHasNoDefaultCardIssue}
              onCardActionPendingChange={setIsCardActionPending}
            />

            <FormCouponPrice
              couponCode={couponCode}
              appliedCoupon={appliedCoupon}
              couponError={couponError}
              handleCouponCode={handleCouponCode}
              isAnyProductSelected={isAnyProductSelected}
              resetCoupon={resetCoupon}
              handleApplyCoupon={handleApplyCoupon}
              loadingCoupon={loadingCoupon}
            />

            <PurchaseSummary
              products={products}
              period={period}
              selectedAddons={selectedAddons}
              counts={counts}
              formatCurrency={formatCurrency}
              calculatePrice={calculatePrice}
              subtotal={subtotal}
              appliedCoupon={appliedCoupon}
            />

            <Button
              color="primary"
              size="medium"
              template="primary"
              typeStyle="rounded"
              className="success"
              type="button"
              onClick={handlePurchase}
              isDisabled={
                !isPropertyStepValid ||
                Object.values(counts).every((count) => count === 0) ||
                !selectedPaymentMethod ||
                hasNoDefaultCardIssue ||
                isCardActionPending ||
                isPurchasing
              }
            >
              {isPurchasing ? 'Procesando compra...' : 'Comprar'}
            </Button>
          </>
        )}
      </main>
    </>
  );
};
